import {
  CheckCircleOutlineOutlined,
  ChevronRight,
  DangerousOutlined,
  ErrorOutlined,
  ExpandLess,
  ExpandMore,
  InfoOutlined,
  NewReleasesOutlined,
  Person,
  Phishing,
  ReportOutlined,
  Settings,
  Sms,
} from "@mui/icons-material";
import { Alert, AlertColor, AlertProps, AlertTitle, ButtonBase, SxProps, Theme } from "@mui/material";
import React, { ReactNode, useState } from "react";
import { Link as RouterLink } from "react-router";
import BNRichText from "../BNRichText/BNRichText";
import BNBox from "../BNBox/BNBox";

export interface UmbracoLink {
  name: string;
  url: string;
  type?: "Content" | "External";
  target?: "_blank";
  /** @example "umb://document/da1dd174106241548f52cbb30b3f09b6" */
  udi?: `umb://document/${string}`;
}

export interface UmbracoNotification {
  key?: string;
  title?: string;
  children: AlertProps["children"];
  detailedText?: string;
  link?: UmbracoLink;
  severity: AlertColor;
  customIcon?: "chat" | "dangerous" | "error" | "person" | "phishing" | "settings" | ReactNode;
  pathList?: string[];
}

interface Props extends UmbracoNotification {
  id?: string;
  resolveUrl?: (inputPath: string) => string;
  isSpa?: (link: UmbracoLink) => boolean;
  onClick?: () => void;
  sx?: SxProps<Theme>;
  style?: AlertProps["style"];
  onClose?: AlertProps["onClose"];
  dataUit?: string;
}

const ButtonBaseWithRouter = (props: any) => <ButtonBase {...props} LinkComponent={RouterLink} />;
const BNAlert = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      title,
      children,
      detailedText,
      severity,
      link,
      customIcon,
      id,
      resolveUrl,
      isSpa = () => false,
      onClick,
      sx,
      style,
      onClose,
      dataUit,
    },
    ref,
  ) => {
    const [showDetailedText, setShowDetailedText] = useState<boolean>(false);
    const ariaLabelId = `label-${id}`;
    const buttonProps = () =>
      link && resolveUrl
        ? {
            component: isSpa(link) ? ButtonBaseWithRouter : ButtonBase,
            href: isSpa(link) ? undefined : resolveUrl(link.url),
            to: isSpa(link) ? resolveUrl(link.url) : undefined,
            target: link.target || undefined,
            focusRipple: true,
            onClick,
            "aria-labelledby": ariaLabelId,
          }
        : detailedText
          ? {
              component: ButtonBaseWithRouter,
              focusRipple: true,
              onClick: () => {
                if (onClick) {
                  onClick();
                }
                setShowDetailedText(!showDetailedText);
              },
              "aria-labelledby": ariaLabelId,
            }
          : {};
    const isExpandable = detailedText && !link;

    const modifixedSx: SxProps<Theme> = [
      {
        borderRadius: 2,
        textAlign: "left",
        width: "100%",
        borderStyle: "none",
        borderWidth: "0.1rem",

        backgroundColor: `${severity}.light`,
        color: "text.primary",
        ".MuiAlert-icon": {
          color: severity === "info" ? "text.primary" : `${severity}.dark`,
        },
        ".MuiAlert-action, .MuiAlert-icon": {
          alignSelf: isExpandable ? "flex-start" : undefined,
        },
        "&:hover": {
          backgroundColor: link || detailedText ? `${severity}.lighter` : undefined,
        },
      },
      // You cannot spread `sx` directly because `SxProps` (typeof sx) can be an array.
      ...(Array.isArray(sx) ? sx : [sx]),
    ];
    return (
      <Alert
        sx={modifixedSx}
        style={style}
        variant="filled"
        severity={severity}
        iconMapping={{
          error: <NewReleasesOutlined />,
          warning: <ReportOutlined />,
          info: <InfoOutlined />,
          success: <CheckCircleOutlineOutlined />,
        }}
        icon={ResolveIcon(customIcon)}
        action={link ? <ChevronRight /> : isExpandable ? showDetailedText ? <ExpandLess /> : <ExpandMore /> : undefined}
        {...buttonProps()}
        onClose={onClose}
        ref={ref}
        data-uit={dataUit}
      >
        {title && <AlertTitle sx={{ fontWeight: "bold", marginBottom: 0 }}>{title}</AlertTitle>}
        <BNBox
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
          id={ariaLabelId}
        >
          {children}
        </BNBox>
        {isExpandable && showDetailedText && <BNRichText html={detailedText} />}
      </Alert>
    );
  },
);

export default BNAlert;

function ResolveIcon(customIcon: UmbracoNotification["customIcon"]) {
  if (!customIcon) {
    return;
  }
  if (typeof customIcon === "string") {
    switch (customIcon) {
      case "chat":
        return <Sms />;
      case "dangerous":
        return <DangerousOutlined />;
      case "error":
        return <ErrorOutlined />;
      case "person":
        return <Person />;
      case "phishing":
        return <Phishing />;
      case "settings":
        return <Settings />;
    }
  }
  return customIcon;
}
